import { cldImg } from "../../../lib/cloudinary";
import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState, useContext } from "react";
import axios from "axios";
import Header from "../../../components/Header";
import { promoActive, AvantPromo, prixAvantPromo, pourcentagePromo, BandeauPromo } from "../../../components/PromoTemp"; // PROMO TEMP
import { AuthContext } from "../../utils/AuthContext";
import toast from "react-hot-toast";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import {
  Calendar, Moon, ShieldCheck, ShieldX, Shield,
  MapPin, Users, ChevronLeft, Check, Info, CalendarRange, Clock
} from "lucide-react";

const G      = "var(--k-green)";
const G_DARK = "var(--k-green-hover)";
const API    = "https://api.komorotel.com";

const KMF_PER_EUR = 491.96775;
const toKMF = (eur) => eur ? Math.round(eur * KMF_PER_EUR).toLocaleString("fr-FR") : null;

const fmtDate  = d => new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "long",  year: "numeric" });
const fmtShort = d => new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "short" });

/* ═══════════════════════════════════════════════════════════
   🆕 PÉRIODES

   Sur un tarif forfaitaire, la réservation n'est plus décrite par deux dates
   mais par une arrivée et un nombre de périodes. La date de départ est
   calculée par le serveur : cette page l'affiche, elle ne la choisit pas.

   Conséquence sur la réservation : le POST envoie `periods` au lieu de
   `checkOutDate`. Envoyer une date de sortie ne servirait à rien, le serveur
   la recalcule de toute façon à partir du plan.
   ═══════════════════════════════════════════════════════════ */
const UNIT_NIGHTS = { short_stay: 1, weekly: 7,          monthly: 30     };
const UNIT_LABEL  = { short_stay: "nuit", weekly: "semaine", monthly: "mois" };

const isPeriodMode = (m) => m === "weekly" || m === "monthly";

// "mois" est invariable
const unitPlural = (mode, n) => {
  const l = UNIT_LABEL[mode];
  if (l === "mois") return l;
  return n > 1 ? `${l}s` : l;
};

export default function Checkout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const params   = new URLSearchParams(location.search);

  const roomId = params.get("roomId");
  const checkIn    = params.get("checkIn");
  const urlCheckOut = params.get("checkOut");
  const ratePlanId = params.get("ratePlanId");
  // ✅ adults/children depuis l'URL
  const adults     = parseInt(params.get("adults"))   || 1;
  const children   = parseInt(params.get("children")) || 0;

  // 🆕 PÉRIODES
  const stayMode = params.get("stayMode") || "short_stay";
  const periods  = parseInt(params.get("periods")) || 1;
  const period   = isPeriodMode(stayMode);

  const [room,           setRoom]           = useState(null);
  const [ratePlan,       setRatePlan]       = useState(null);
  const [total,          setTotal]          = useState(null);
  const [totalKMF,       setTotalKMF]       = useState(null);
  const [pricePerNight,  setPricePerNight]  = useState(null);
  const [pricePerNightKMF, setPricePerNightKMF] = useState(null);
  const [loadingReserve, setLoadingReserve] = useState(false);
  const [isUserReady,    setIsUserReady]    = useState(false);
  const [agreed,         setAgreed]         = useState(false);
  const [taxTotal,  setTaxTotal]  = useState(null);
  const [taxConfig, setTaxConfig] = useState(null);

  // 🆕 PÉRIODES — la date de sortie vient du serveur sur un forfait.
  // Celle de l'URL n'est qu'un repère d'affichage transmis par la page hôtel.
  const [serverCheckOut, setServerCheckOut] = useState(null);
  const checkOut = period ? (serverCheckOut || urlCheckOut) : urlCheckOut;

  const totalSansTaxe = taxConfig?.enabled && !taxConfig.included
    ? parseFloat((total - taxTotal).toFixed(2))
    : total;

  const [guest, setGuest] = useState({
    firstName: "", lastName: "", email: "", phone: ""
  });

  /* 🆕 MISSION — le voyageur arrive par le lien de son mail de mission.
     Le jeton a été déposé par la page /mission/:token : il prouve qui il
     est, donc ni compte ni vérification d'email ne lui sont redemandés. */
  const [mission] = useState(() => {
    try {
      const brut = sessionStorage.getItem("komo-mission");
      return brut ? JSON.parse(brut) : null;
    } catch {
      return null;
    }
  });

  // 🆕 PÉRIODES — sur un forfait la durée est connue sans dépendre de checkOut
  const nights = period
    ? periods * UNIT_NIGHTS[stayMode]
    : (checkIn && checkOut ? Math.ceil((new Date(checkOut) - new Date(checkIn)) / 86400000) : 0);

  // Libellé de durée : "2 mois (60 nuits)" sur un forfait, "3 nuits" sinon.
  const durationLabel = period
    ? `${periods} ${unitPlural(stayMode, periods)} · ${nights} nuits`
    : `${nights} nuit${nights > 1 ? "s" : ""}`;

  // 🆕 VERSEMENT — montant attendu avant confirmation : le forfait de la
  // PREMIÈRE période, hors taxe de séjour. Calculé ici à titre indicatif,
  // le serveur reste la source de vérité.
  const depositEstimate = period && totalSansTaxe
    ? Math.round((totalSansTaxe / periods) * 100) / 100
    : null;

  // Label voyageurs
  const guestsLabel = [
    adults > 0   ? `${adults} adulte${adults > 1 ? "s" : ""}`     : null,
    children > 0 ? `${children} enfant${children > 1 ? "s" : ""}` : null,
  ].filter(Boolean).join(" · ");

  /* ── Fetch user ── */
  useEffect(() => {
    if (mission?.traveller) {
      setGuest(g => ({
        ...g,
        firstName: mission.traveller.firstName || g.firstName,
        lastName:  mission.traveller.lastName  || g.lastName,
        email:     mission.traveller.email     || g.email,
      }));
      setIsUserReady(true);
      return;
    }
    if (!user) { setIsUserReady(true); return; }
    axios.get(`${API}/api/user/${user._id}`, { withCredentials: true })
      .then(r => {
        setGuest({ firstName: r.data.name || "", lastName: r.data.surname || "", email: r.data.email || "", phone: r.data.phone || "" });
        setIsUserReady(true);
      })
      .catch(() => setIsUserReady(true));
  }, [user, mission]);

  /* ── Fetch room ── */
  useEffect(() => {
    if (!roomId) return;
    axios.get(`${API}/api/rooms/${roomId}`).then(r => setRoom(r.data)).catch(() => {});
  }, [roomId]);

  /* ── Fetch prix + rate plan ── */
  useEffect(() => {
    if (!roomId || !ratePlanId || !checkIn) return;
    // 🆕 PÉRIODES — sur un court séjour, checkOut reste indispensable.
    if (!period && !urlCheckOut) return;

    // 🆕 PÉRIODES — le calcul prend stayMode + periods sur un forfait,
    // les deux dates sinon.
    const priceParams = period
      ? { roomId, ratePlanId, checkIn, stayMode, periods, adults, children }
      : { roomId, ratePlanId, checkIn, checkOut: urlCheckOut, adults, children };

    Promise.all([
      axios.get(`${API}/api/rateprices/calculate`, { params: priceParams }),
      axios.get(`${API}/api/rateplans/single/${ratePlanId}`, { withCredentials: true }),
    ]).then(([priceRes, planRes]) => {
      setTotal(priceRes.data.total);
      setTotalKMF(priceRes.data.totalKMF);
      setPricePerNight(priceRes.data.pricePerNight);
      setPricePerNightKMF(priceRes.data.pricePerNightKMF);
      setRatePlan(planRes.data);
      setTaxTotal(priceRes.data.taxTotal);
      setTaxConfig(priceRes.data.taxConfig);
      // 🆕 PÉRIODES — date de sortie faisant foi
      if (priceRes.data.checkOut) setServerCheckOut(priceRes.data.checkOut);
    }).catch(() => {});
  }, [roomId, ratePlanId, checkIn, urlCheckOut, adults, children, stayMode, periods, period]);

  /* ── Politique annulation ── */
  // 🆕 PÉRIODES — un délai stocké en heures s'affiche en jours sur un forfait
  // (façon Airbnb "30 jours avant l'arrivée"), en heures sinon.
  const formatDelay = (hours, isLongStay) => {
    if (isLongStay) {
      const d = Math.round(hours / 24);
      return `${d} jour${d > 1 ? "s" : ""}`;
    }
    return `${hours}h`;
  };

  const getPolicyInfo = () => {
    if (!ratePlan) return null;
    // 🆕 FRAIS FIXES — on récupère penaltyMode + penaltyFixedAmount en plus de penaltyPercentage
    const { type, hoursBeforeArrival, penaltyMode, penaltyPercentage, penaltyFixedAmount } = ratePlan.cancellationPolicy;
    // 🆕 PÉRIODES — les valeurs sont désormais weekly / monthly.
    // "long_stay" est conservé pour les plans d'avant migration.
    const isLongStay = ratePlan.stayType && ratePlan.stayType !== "short_stay";
    const delayLabel = formatDelay(hoursBeforeArrival, isLongStay);
    const hoursLeft = (new Date(checkIn) - new Date()) / 3600000;
    if (type === "free") return {
      icon: <ShieldCheck size={16} color={G} />,
      label: `Annulation gratuite jusqu'à ${delayLabel} avant`,
      sub: hoursLeft >= hoursBeforeArrival
        ? "Remboursement total si annulation maintenant"
        : "Délai d'annulation gratuite dépassé",
      color: hoursLeft >= hoursBeforeArrival ? G : "#ef4444",
      bg:    hoursLeft >= hoursBeforeArrival ? "var(--k-green-soft)" : "#fef2f2",
    };
    if (type === "partial") {
      // 🆕 FRAIS FIXES — fallback "percentage" pour compat avec d'anciens plans sans penaltyMode
      const mode = penaltyMode || "percentage";
      const isFixed = mode === "fixed";
      const feeText = isFixed ? `${penaltyFixedAmount}€ de frais` : `${penaltyPercentage}% de frais`;
      const refundSub = isFixed
        ? `Remboursement du solde après déduction de ${penaltyFixedAmount}€ passé ${delayLabel}`
        : `Remboursement ${100 - penaltyPercentage}% passé ${delayLabel}`;
      return {
        icon:  <Shield size={16} color="#f59e0b" />,
        label: `Annulation partielle — ${feeText}`,
        sub:   refundSub,
        color: "#d97706", bg: "#fffbeb",
      };
    }
    return {
      icon:  <ShieldX size={16} color="#ef4444" />,
      label: "Non remboursable",
      sub:   "Aucun remboursement en cas d'annulation",
      color: "#ef4444", bg: "#fef2f2",
    };
  };

  const policy = getPolicyInfo();

  const MEAL_LABELS = {
    breakfast:  { emoji: "🍳",  label: "Petit-déjeuner inclus" },
    half_board: { emoji: "🍽️", label: "Demi-pension"           },
    full_board: { emoji: "🍽️", label: "Pension complète"       },
  };

  /* ── Submit ── */
  const handleReserve = async () => {
    if (!guest.email || !guest.firstName || !guest.lastName) {
      toast.error("Veuillez remplir tous les champs obligatoires"); return;
    }
    if (!guest.phone || !guest.phone.startsWith("+")) {
      toast.error("Numéro de téléphone invalide"); return;
    }
    if (!agreed) {
      toast.error("Veuillez accepter les conditions"); return;
    }
    try {
      setLoadingReserve(true);
      const res = await axios.post(`${API}/api/reservation`,
        {
          roomId,
          ratePlanId,
          checkInDate: checkIn,
          // 🆕 PÉRIODES — sur un forfait c'est `periods` qui décrit le séjour.
          // Le serveur en déduit la date de sortie ; lui envoyer checkOutDate
          // serait au mieux ignoré, au pire trompeur.
          ...(period ? { periods } : { checkOutDate: urlCheckOut }),
          // ✅ Envoyer guests dans le body
          guests: { adults, children },
          guest,
          // 🆕 MISSION — vaut identité du voyageur et rattachement payeur
          ...(mission?.token ? { missionToken: mission.token } : {}),
        },
        { withCredentials: true }
      );
      const resa = res.data;
      // 🆕 VERSEMENT — une réservation sur forfait part en attente de paiement.
      // Les nuits sont déjà bloquées, il n'y a pas de lien à confirmer : on
      // envoie donc le client sur sa confirmation, qui porte les instructions.
      if (resa.status === "awaiting_deposit") navigate("/confirmation/" + resa._id);
      else if (resa.status === "pending") navigate("/reservation-pending", { state: { email: resa.guest.email } });
      else navigate("/confirmation/" + resa._id);
    } catch (err) {
      toast.error(err.response?.data?.error || err.response?.data?.message || "Une erreur est survenue");
    } finally {
      setLoadingReserve(false);
    }
  };

  const isLoading = !room || !ratePlan || !isUserReady;

  // PROMO TEMP — affichage seulement, le prix reçu est déjà réduit
  const promo = promoActive({ hotelId: room?.hotelId?._id || room?.hotelId, date: checkIn });
  const economie = promo && totalSansTaxe ? prixAvantPromo(totalSansTaxe) - totalSansTaxe : 0;

  return (
    <>
      <Header />
      <div style={{ minHeight: "100vh", background: "#f9fafb", fontFamily: "'DM Sans','Inter',system-ui,sans-serif", paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 960, margin: "0 auto", padding: "0 24px" }}>

          {/* ── Breadcrumb ── */}
          <button onClick={() => navigate(-1)}
            style={{ display: "flex", alignItems: "center", gap: 6, background: "none", border: "none", cursor: "pointer", color: "#6b7280", fontSize: 13, fontWeight: 500, marginBottom: 24, padding: 0 }}>
            <ChevronLeft size={16} /> Retour
          </button>

          <h1 style={{ fontFamily: "Georgia, serif", fontSize: 26, fontWeight: 700, color: "#111827", marginBottom: 4 }}>
            Finaliser la réservation
          </h1>
          <p style={{ fontSize: 13, color: "#9ca3af", marginBottom: 32 }}>Vérifiez les détails avant de confirmer</p>

          {/* ── Étapes ── */}
          <div style={{ display: "flex", alignItems: "center", gap: 0, marginBottom: 36, overflow: "hidden" }}>
            {["Votre séjour", "Informations", "Confirmation"].map((step, i) => (
              <React.Fragment key={i}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                  <div style={{
                    width: 28, height: 28, borderRadius: "50%",
                    background: i === 0 ? G : i === 1 ? G : "#f3f4f6",
                    color: i < 2 ? "#fff" : "#9ca3af",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 12, fontWeight: 700, flexShrink: 0,
                  }}>
                    {i < 1 ? <Check size={13} /> : i + 1}
                  </div>
                  <span className="co-step-label" style={{ fontSize: 13, fontWeight: i < 2 ? 600 : 400, color: i < 2 ? "#111827" : "#9ca3af", whiteSpace: "nowrap" }}>
                    {step}
                  </span>
                </div>
                {i < 2 && <div style={{ flex: 1, height: 1, background: i < 1 ? G : "#e5e7eb", margin: "0 6px", minWidth: 12 }} />}
              </React.Fragment>
            ))}
          </div>

          {isLoading ? (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
              {[...Array(4)].map((_, i) => (
                <div key={i} style={{ height: i % 2 === 0 ? 180 : 320, borderRadius: 16, background: "linear-gradient(90deg,#f3f4f6 25%,#e9eaec 50%,#f3f4f6 75%)", backgroundSize: "200% 100%", animation: "shimmer 1.4s infinite" }} />
              ))}
              <style>{`@keyframes shimmer{to{background-position:-200% 0}}`}</style>
            </div>
          ) : (
            <div className="checkout-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, alignItems: "start" }}>

              {/* ─── COLONNE GAUCHE — Résumé ─── */}
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                {promo && <BandeauPromo texte={economie > 0 ? `Promo appliquée : vous économisez ${economie}€` : "Promo appliquée"} />}{/* PROMO TEMP */}

                {/* Card chambre */}
                <div style={{ background: "#fff", borderRadius: 16, border: "1px solid #f3f4f6", padding: "20px", boxShadow: "0 2px 10px rgba(0,0,0,0.04)" }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#9ca3af", letterSpacing: "0.08em", textTransform: "uppercase" }}>Votre séjour</div>
                    {/* 🆕 PÉRIODES — rappel du mode choisi */}
                    {period && (
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 700, color: G, background: "var(--k-green-soft)", borderRadius: 20, padding: "3px 10px" }}>
                        <CalendarRange size={12} />
                        {periods} {unitPlural(stayMode, periods)}
                      </span>
                    )}
                  </div>

                  {room && (
                    <div style={{ display: "flex", gap: 14 }}>
                      <div style={{ width: 80, height: 80, borderRadius: 12, overflow: "hidden", flexShrink: 0, background: "#f3f4f6" }}>
                        {room.photos?.[0] && <img src={cldImg(room.photos[0], 200)} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 14, fontWeight: 700, color: "#111827", marginBottom: 8 }}>{room.title}</div>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#6b7280", marginBottom: 5 }}>
                          <Calendar size={13} color="#9ca3af" />
                          <span>{fmtShort(checkIn)} → {checkOut ? fmtShort(checkOut) : "…"}</span>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#6b7280", marginBottom: 5 }}>
                          <Moon size={13} color="#9ca3af" />
                          <span>{durationLabel}</span>
                        </div>
                        {/* ✅ Voyageurs */}
                        <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#6b7280" }}>
                          <Users size={13} color="#9ca3af" />
                          <span>{guestsLabel}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Dates détaillées */}
                  <div style={{ marginTop: 16, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                    {[
                      { label: "Arrivée", val: fmtDate(checkIn) },
                      // 🆕 PÉRIODES — sur un forfait, la sortie est calculée
                      { label: period ? "Départ (calculé)" : "Départ", val: checkOut ? fmtDate(checkOut) : "…" },
                    ].map((d, i) => (
                      <div key={i} style={{ background: "#f9fafb", borderRadius: 10, padding: "10px 12px" }}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: "#9ca3af", letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 3 }}>{d.label}</div>
                        <div style={{ fontSize: 12, fontWeight: 600, color: "#111827" }}>{d.val}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Card tarif */}
                {ratePlan && (
                  <div style={{ background: "#fff", borderRadius: 16, border: "1px solid #f3f4f6", padding: "20px", boxShadow: "0 2px 10px rgba(0,0,0,0.04)" }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#9ca3af", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 14 }}>Tarif sélectionné</div>

                    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, marginBottom: 14 }}>
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: "#111827", marginBottom: 4 }}>{ratePlan.name}</div>
                        {ratePlan.mealPlan && ratePlan.mealPlan !== "none" && MEAL_LABELS[ratePlan.mealPlan] && (
                          <div style={{ fontSize: 12, color: "#6b7280" }}>
                            {MEAL_LABELS[ratePlan.mealPlan].emoji} {MEAL_LABELS[ratePlan.mealPlan].label}
                          </div>
                        )}
                        {/* ✅ Supplément voyageurs si applicable —
                            🆕 PÉRIODES : masqué sur un forfait, où le prix couvre
                            le logement et non le nombre d'occupants. */}
                        {!period && (ratePlan.extraGuestPolicy?.extraAdultFee > 0 || ratePlan.extraGuestPolicy?.extraChildFee > 0) && (
                          <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4 }}>
                            {adults > (ratePlan.extraGuestPolicy?.baseOccupancy || 1) && (
                              <span>+{ratePlan.extraGuestPolicy.extraAdultFee}€/adulte sup. · </span>
                            )}
                            {children > 0 && ratePlan.extraGuestPolicy?.extraChildFee > 0 && (
                              <span>+{ratePlan.extraGuestPolicy.extraChildFee}€/enfant</span>
                            )}
                          </div>
                        )}
                      </div>
                      <div style={{ textAlign: "right", flexShrink: 0 }}>
                        {/* 🆕 PÉRIODES — un forfait s'affiche au prix de sa période,
                            jamais divisé par le nombre de nuits. */}
                        {period ? (
                          <>
                            {promo && depositEstimate && <div style={{ marginBottom: 4 }}><AvantPromo prix={depositEstimate} /></div>}{/* PROMO TEMP */}
                            <div style={{ fontSize: 20, fontWeight: 800, color: "#111827", lineHeight: 1 }}>
                              {depositEstimate ? `${depositEstimate}€` : "..."}
                            </div>
                            <div style={{ fontSize: 11, color: "#9ca3af" }}>/ {UNIT_LABEL[stayMode]}</div>
                            {pricePerNight && (
                              <div style={{ fontSize: 10, color: "#9ca3af" }}>soit {pricePerNight}€/nuit</div>
                            )}
                          </>
                        ) : (
                          <>
                            {promo && <div style={{ marginBottom: 4 }}><AvantPromo prix={pricePerNight} /></div>}{/* PROMO TEMP */}
                            <div style={{ fontSize: 20, fontWeight: 800, color: "#111827", lineHeight: 1 }}>{pricePerNight}€</div>
                            <div style={{ fontSize: 11, color: "#9ca3af" }}>/ nuit</div>
                            {pricePerNightKMF && (
                              <div style={{ fontSize: 10, color: "#9ca3af" }}>{pricePerNightKMF?.toLocaleString("fr-FR")} KMF</div>
                            )}
                          </>
                        )}
                      </div>
                    </div>

                    {/* Politique */}
                    {policy && (
                      <div style={{ background: policy.bg, borderRadius: 10, padding: "10px 12px", display: "flex", alignItems: "flex-start", gap: 10 }}>
                        {policy.icon}
                        <div>
                          <div style={{ fontSize: 12, fontWeight: 600, color: policy.color }}>{policy.label}</div>
                          <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>{policy.sub}</div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* 🆕 VERSEMENT — ce qui se passe après la confirmation.
                    Le client doit comprendre AVANT de valider que sa réservation
                    ne sera pas confirmée immédiatement, et que les dates sont
                    malgré tout bloquées à son nom. */}
                {period && depositEstimate && (
                  <div style={{ background: "#fffbeb", borderRadius: 16, border: "1px solid #fde68a", padding: "18px 20px" }}>
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                      <Clock size={18} color="#d97706" style={{ flexShrink: 0, marginTop: 1 }} />
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: "#92400e", marginBottom: 6 }}>
                          Réservation à confirmer par l'établissement
                        </div>
                        <p style={{ fontSize: 12, color: "#78350f", lineHeight: 1.6, margin: 0 }}>
                          Vos dates seront bloquées à votre nom dès la validation. Pour confirmer
                          définitivement le séjour, un règlement de <strong>{depositEstimate}€</strong>{" "}
                          {periods > 1 ? `(le premier ${UNIT_LABEL[stayMode]})` : ""} se fait
                          directement auprès de l'établissement, qui vous contactera.
                        </p>
                        <p style={{ fontSize: 11, color: "#a16207", lineHeight: 1.6, margin: "8px 0 0" }}>
                          Sans règlement dans le délai indiqué par mail, les dates repartent
                          automatiquement à la vente.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Card total */}
                <div style={{ background: "#fff", borderRadius: 16, border: "1px solid #f3f4f6", padding: "20px", boxShadow: "0 2px 10px rgba(0,0,0,0.04)" }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#9ca3af", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 14 }}>Récapitulatif</div>

                  {/* 🆕 PÉRIODES — la ligne suit l'unité réellement facturée */}
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: "#6b7280", marginBottom: 8 }}>
                    <span>
                      {period
                        ? `${depositEstimate}€ × ${periods} ${unitPlural(stayMode, periods)}`
                        : `${pricePerNight}€ × ${nights} nuit${nights > 1 ? "s" : ""}`}
                    </span>
                    <span>{totalSansTaxe}€</span>
                  </div>
                  {/* PROMO TEMP */}
                  {promo && economie > 0 && (
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 8 }}>
                      <span style={{ color: "#6b7280" }}>Promo −{pourcentagePromo}% sur komorotel.com <span style={{ color: "#9ca3af" }}>(déjà déduite)</span></span>
                      <span style={{ color: G, fontWeight: 700 }}>−{economie}€</span>
                    </div>
                  )}
                  {/* ✅ Ligne voyageurs si supplément */}
                  {guestsLabel && (
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "#9ca3af", marginBottom: 8 }}>
                      <span>{guestsLabel}</span>
                      <span>Inclus</span>
                    </div>
                  )}
                  {taxConfig?.enabled && (
                    taxConfig.included ? (
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "#9ca3af", marginBottom: 14 }}>
                        <span>Taxe de séjour</span>
                        <span style={{ color: G, fontWeight: 600 }}>Incluse</span>
                      </div>
                    ) : (
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "#6b7280", marginBottom: 14 }}>
                        <span>Taxe de séjour ({adults + children} pers. × {nights} nuits)</span>
                        <span style={{ fontWeight: 600 }}>+{taxTotal?.toFixed(2)}€</span>
                      </div>
                    )
                  )}

                  <div style={{ borderTop: "1px solid #f3f4f6", paddingTop: 14, display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                    <span style={{ fontSize: 14, fontWeight: 700, color: "#111827" }}>Total</span>
                    <div style={{ textAlign: "right" }}>
                      <div style={{ fontFamily: "Georgia, serif", fontSize: 26, fontWeight: 700, color: "#111827" }}>
                        {total || "..."}€
                      </div>
                      {/* ✅ KMF total */}
                      {totalKMF && (
                        <div style={{ fontSize: 12, color: "#9ca3af" }}>
                          ≈ {totalKMF?.toLocaleString("fr-FR")} KMF
                        </div>
                      )}
                    </div>
                  </div>

                  <div style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#9ca3af" }}>
                    <Info size={12} color="#9ca3af" />
                    Aucun paiement prélevé aujourd'hui
                  </div>
                </div>
              </div>

              {/* ─── COLONNE DROITE — Formulaire ─── */}
              <div style={{ background: "#fff", borderRadius: 16, border: "1px solid #f3f4f6", padding: "24px", boxShadow: "0 2px 10px rgba(0,0,0,0.04)" }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#9ca3af", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 20 }}>Informations client</div>

                {/* 🆕 MISSION — l'enveloppe, et où se situe ce séjour dedans.
                    Quelqu'un qui voit son dépassement avant de valider peut
                    encore changer d'hébergement ; après, il fait attendre
                    son responsable. */}
                {mission && (() => {
                  const parNuit = pricePerNight;
                  const trop = mission.maxPricePerNight && parNuit > mission.maxPricePerNight;
                  const tropLong = mission.maxNights && nights > mission.maxNights;
                  const dedans = !trop && !tropLong;
                  return (
                    <div style={{
                      marginBottom: 20, padding: "14px 16px", borderRadius: 12,
                      background: dedans ? "var(--k-green-soft)" : "#FFF8EC",
                      border: `1px solid ${dedans ? "rgb(var(--k-green-rgb)/0.2)" : "#F5E3C0"}`,
                    }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: "#111827", marginBottom: 4 }}>
                        Pris en charge par {mission.organizationName}
                      </div>
                      <div style={{ fontSize: 12, color: "#6b7280" }}>
                        Mission {mission.title}
                      </div>

                      {mission.maxPricePerNight && (
                        <div style={{ fontSize: 12, marginTop: 8, color: trop ? "#A5660B" : "#4b5563" }}>
                          {trop
                            ? `${parNuit}€ la nuit, au-delà des ${mission.maxPricePerNight}€ prévus`
                            : `${parNuit}€ la nuit, budget de ${mission.maxPricePerNight}€ respecté`}
                        </div>
                      )}
                      {tropLong && (
                        <div style={{ fontSize: 12, marginTop: 4, color: "#A5660B" }}>
                          {nights} nuits demandées, {mission.maxNights} autorisées
                        </div>
                      )}

                      <div style={{ fontSize: 11.5, marginTop: 8, color: "#6b7280", lineHeight: 1.5 }}>
                        {dedans
                          ? "Votre réservation sera confirmée immédiatement."
                          : "Hors de l'enveloppe : votre responsable devra valider avant que le séjour soit acquis."}
                      </div>
                    </div>
                  );
                })()}

                {!isUserReady ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    {[...Array(4)].map((_, i) => (
                      <div key={i} style={{ height: 44, borderRadius: 10, background: "#f3f4f6", animation: "shimmer 1.4s infinite" }} />
                    ))}
                  </div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>

                    {user && (
                      <div style={{ display: "flex", alignItems: "center", gap: 8, background: "var(--k-green-soft)", borderRadius: 10, padding: "8px 12px", marginBottom: 4 }}>
                        <Check size={13} color={G} />
                        <span style={{ fontSize: 12, color: G, fontWeight: 500 }}>Informations pré-remplies depuis votre compte</span>
                      </div>
                    )}

                    {[
                      { key: "lastName",  placeholder: "Nom *",    type: "text"  },
                      { key: "firstName", placeholder: "Prénom *",  type: "text"  },
                      { key: "email",     placeholder: "Email *",   type: "email" },
                    ].map(f => (
                      <div key={f.key} style={{ position: "relative" }}>
                        <input
                          type={f.type}
                          value={guest[f.key]}
                          onChange={e => setGuest({ ...guest, [f.key]: e.target.value })}
                          placeholder={f.placeholder}
                          style={{
                            width: "100%", border: "1px solid #e5e7eb", borderRadius: 10,
                            padding: "11px 14px", fontSize: 13, outline: "none",
                            fontFamily: "inherit", color: "#111827",
                            boxSizing: "border-box", transition: "border-color 0.18s",
                          }}
                          onFocus={e => e.target.style.borderColor = G}
                          onBlur={e => e.target.style.borderColor = "#e5e7eb"}
                        />
                      </div>
                    ))}

                    <PhoneInput
                      country="km"
                      value={guest.phone?.replace("+", "")}
                      onChange={phone => setGuest(p => ({ ...p, phone: "+" + phone }))}
                      inputStyle={{ width: "100%", height: 44, borderRadius: 10, border: "1px solid #e5e7eb", fontSize: 13, fontFamily: "inherit" }}
                      buttonStyle={{ borderTopLeftRadius: 10, borderBottomLeftRadius: 10, border: "1px solid #e5e7eb", borderRight: "none" }}
                    />

                    {/* 🆕 VERSEMENT — sur un forfait, le téléphone n'est plus
                        un simple confort : c'est par là que l'établissement
                        joindra le client pour le règlement. */}
                    {period && (
                      <p style={{ fontSize: 11, color: "#9ca3af", margin: "-4px 0 0", lineHeight: 1.5 }}>
                        L'établissement vous contactera sur ces coordonnées pour convenir du règlement.
                      </p>
                    )}

                    <label style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer", marginTop: 4 }}>
                      <div
                        onClick={() => setAgreed(!agreed)}
                        style={{
                          width: 18, height: 18, borderRadius: 5, border: `2px solid ${agreed ? G : "#d1d5db"}`,
                          background: agreed ? G : "#fff", flexShrink: 0, marginTop: 1,
                          display: "flex", alignItems: "center", justifyContent: "center",
                          transition: "all 0.15s", cursor: "pointer",
                        }}>
                        {agreed && <Check size={11} color="#fff" strokeWidth={3} />}
                      </div>
                      <span style={{ fontSize: 12, color: "#6b7280", lineHeight: 1.5 }}>
                        J'accepte les{" "}
                        <a href="/conditions" target="_blank" style={{ color: G, fontWeight: 600, textDecoration: "none" }}>conditions générales</a>
                        {" "}et la politique d'annulation du tarif sélectionné.
                      </span>
                    </label>

                    <button
                      onClick={handleReserve}
                      disabled={loadingReserve}
                      style={{
                        marginTop: 8, width: "100%", background: loadingReserve ? "#d1d5db" : G,
                        color: "#fff", border: "none", borderRadius: 12,
                        padding: "14px 0", fontSize: 14, fontWeight: 700,
                        cursor: loadingReserve ? "not-allowed" : "pointer",
                        transition: "background 0.18s",
                      }}
                      onMouseEnter={e => { if (!loadingReserve) e.currentTarget.style.backgroundColor = G_DARK; }}
                      onMouseLeave={e => { if (!loadingReserve) e.currentTarget.style.backgroundColor = G; }}>
                      {loadingReserve
                        ? "Réservation en cours..."
                        // 🆕 VERSEMENT — le libellé dit ce qui se passe vraiment
                        : period ? "Bloquer ces dates →"
                        : mission ? `Réserver aux frais de ${mission.organizationName}`
                        : "Confirmer la réservation →"}
                    </button>

                    <p style={{ fontSize: 11, textAlign: "center", color: "#9ca3af", marginTop: 4 }}>
                      En confirmant, vous acceptez les conditions du tarif sélectionné.
                    </p>
                  </div>
                )}
              </div>

            </div>
          )}
        </div>
      </div>

      <style>{`
          @keyframes shimmer { to { background-position: -200% 0; } }
          @media (max-width: 768px) {
            .checkout-grid { grid-template-columns: 1fr !important; }
          }
          @media (max-width: 400px) {
            .co-step-label { display: none !important; }
          }
        `}</style>
    </>
  );
}